import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { UserTagsSection } from "features/user/sections/UserTagsSection"

import { useTimezone } from "hooks/useTimezone"

import { TagHistoryRow, UserTagRow } from "services/tags/type"
import { userTagsService } from "services/tags/user-tags.service"

type Props = {
  userId?: string
  refreshKey?: number
}

export const UserTagsTab: FC<Props> = ({ userId, refreshKey }) => {
  const { formatDateTime } = useTimezone()
  const [loading, setLoading] = useState(false)

  // current tags
  const [assigned, setAssigned] = useState<UserTagRow[]>([])

  // history
  const [historyLoading, setHistoryLoading] = useState(false)
  const [history, setHistory] = useState<TagHistoryRow[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const limit = 20

  const loadAssigned = useCallback(async () => {
    if (!userId) return
    try {
      setLoading(true)
      const rows = await userTagsService.getUserTags(userId)
      setAssigned(rows ?? [])
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to load user tags")
    } finally {
      setLoading(false)
    }
  }, [userId])

  const loadHistory = useCallback(
    async (p?: number) => {
      if (!userId) return
      const nextPage = p ?? page

      try {
        setHistoryLoading(true)
        const res = await userTagsService.getHistory(userId, { page: nextPage, limit })
        setHistory(res?.data ?? [])
        setTotalPages(res?.totalPages ?? 0)
        setTotalPages(res?.totalPages ?? 0)
      } catch (e: any) {
        toast.error(e?.data?.message ?? e?.message ?? "Failed to load tag history")
      } finally {
        setHistoryLoading(false)
      }
    },
    [userId, page]
  )

  useEffect(() => {
    if (!userId) return
    setPage(1)
    loadAssigned()
    loadHistory(1)
  }, [userId, refreshKey, loadAssigned, loadHistory])

  const manualTags = useMemo(() => assigned.filter((r) => !r.tag?.isAutomatic), [assigned])
  const autoTags = useMemo(() => assigned.filter((r) => !!r.tag?.isAutomatic), [assigned])

  const onRefreshAll = useCallback(async () => {
    await Promise.all([loadAssigned(), loadHistory(1)])
    setPage(1)
  }, [loadAssigned, loadHistory])

  if (!userId) return null

  return (
    <div className="flex flex-col gap-4">
      <UserTagsSection userId={userId} refreshKey={refreshKey} />

      <div className="w-full rounded-xl border border-[rgba(139,92,246,0.25)] bg-white dark:bg-[#0C0C1A] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Breakdown</div>

          <button className={clsx("btn btn-sm", { "btn-disabled": loading || historyLoading })} onClick={onRefreshAll}>
            Refresh
          </button>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TagBucket title="Manual tags" loading={loading} rows={manualTags} />
          <TagBucket title="Automatic tags" loading={loading} rows={autoTags} />
        </div>
      </div>

      <div className="w-full rounded-xl border border-[rgba(139,92,246,0.25)] bg-white dark:bg-[#0C0C1A] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Tag history</div>

          <div className="flex items-center gap-2">
            <button
              className={clsx("btn btn-sm", { "btn-disabled": historyLoading || page <= 1 })}
              onClick={() => {
                const next = Math.max(1, page - 1)
                setPage(next)
                loadHistory(next)
              }}
            >
              Prev
            </button>

            <div className="text-sm opacity-70">
              Page {totalPages > 0 ? page : 0} / {totalPages}
            </div>

            <button
              className={clsx("btn btn-sm", { "btn-disabled": historyLoading || totalPages === 0 || page >= totalPages })}
              onClick={() => {
                const next = Math.min(totalPages, page + 1)
                setPage(next)
                loadHistory(next)
              }}
            >
              Next
            </button>
          </div>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Date</th>
                <th>Action</th>
                <th>Tag</th>
                <th>Type</th>
                <th>Triggered by</th>
                <th>Note</th>
              </tr>
            </thead>

            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex items-center gap-2">
                      <span className="loading loading-spinner loading-md" />
                      <span>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={6} className="opacity-70">
                    No history yet
                  </td>
                </tr>
              ) : (
                history.map((h) => (
                  <tr key={h.id}>
                    <td className="opacity-80">{formatDateTime(h.created_at)}</td>
                    <td>
                      <span
                        className={clsx("badge", {
                          "badge-success": h.action === "ASSIGNED",
                          "badge-error": h.action === "REMOVED"
                        })}
                      >
                        {h.action}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: h.tag?.color ?? "#64748B" }} />
                        <span className="font-medium">{h.tag?.name ?? h.tag_id}</span>
                        {h.tag?.category ? <span className="opacity-60 text-xs">({h.tag.category})</span> : null}
                      </div>
                    </td>
                    <td>{h.tag?.isAutomatic ? "Automatic" : "Manual"}</td>
                    <td className="opacity-80">
                      {h.triggered_by_type}
                      {h.triggered_by_user_id ? <span className="opacity-60"> · {h.triggered_by_user_id}</span> : null}
                    </td>
                    <td className="opacity-70">{h.note ?? "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const TagBucket: FC<{ title: string; loading: boolean; rows: UserTagRow[] }> = ({ title, loading, rows }) => {
  return (
    <div className="rounded-xl border border-white/10 p-3">
      <div className="text-sm font-semibold opacity-80">{title}</div>

      <div className="mt-2 flex flex-wrap gap-2">
        {loading ? (
          <span className="loading loading-spinner loading-md" />
        ) : rows.length === 0 ? (
          <div className="text-sm opacity-60">None</div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2 rounded-full px-3 py-1 text-sm border border-[rgba(255,255,255,0.1)]"
              style={{ backgroundColor: r.tag?.color ?? "#64748B" }}
              title={r.tag?.description ?? ""}
            >
              <span className="text-white">{r.tag?.name}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
